import { allergenTypeLabel } from "@/lib/allergyLookup";

export const ALLERGY_KIND_ALLERGY = "allergy";
export const ALLERGY_KIND_INTOLERANCE = "intolerance";
export const ALLERGY_KIND_CONTRAINDICATION = "contraindication";

export const ALLERGY_KINDS = [
  { id: ALLERGY_KIND_ALLERGY, label: "Allergy" },
  { id: ALLERGY_KIND_INTOLERANCE, label: "Intolerance" },
  { id: ALLERGY_KIND_CONTRAINDICATION, label: "Contraindication" },
];

export const ALLERGIES_EMPTY = "No allergies on file.";
export const ALLERGIES_NKA = "No known allergies.";

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  resolved: "Resolved",
};

const VERIFICATION_LABELS = {
  unconfirmed: "Unconfirmed",
  confirmed: "Confirmed",
  refuted: "Refuted",
  "entered-in-error": "Entered in error",
};

const CRITICALITY_LABELS = {
  low: "Low",
  high: "High",
  "unable-to-assess": "Unable to assess",
};

const CATEGORY_LABELS = {
  food: "Food",
  medication: "Medication",
  environment: "Environment",
  biologic: "Biologic",
};

const SEVERITY_LABELS = {
  mild: "Mild",
  moderate: "Moderate",
  severe: "Severe",
};

export function kindFromRecord(item) {
  if (item?.al1Type === "MC") return ALLERGY_KIND_CONTRAINDICATION;
  if (item?.type === "intolerance") return ALLERGY_KIND_INTOLERANCE;
  return ALLERGY_KIND_ALLERGY;
}

export function kindLabel(kind) {
  return ALLERGY_KINDS.find((row) => row.id === kind)?.label || "Allergy";
}

export function defaultAl1Type(category, kind) {
  if (kind === ALLERGY_KIND_CONTRAINDICATION) return "MC";
  const cats = Array.isArray(category) ? category : category ? [category] : [];
  if (cats.includes("medication")) return "DA";
  if (cats.includes("food")) return "FA";
  if (cats.includes("environment")) return "EA";
  return "MA";
}

export function defaultCategoryForAllergenType(allergenType) {
  if (allergenType === "drug-ingredient" || allergenType === "drug-class") {
    return "medication";
  }
  return "";
}

export function firstManifestation(item) {
  const reactions = item?.reaction || [];
  for (const row of reactions) {
    const list = row?.manifestation || [];
    for (const manifestation of list) {
      if (manifestation?.text) return manifestation.text;
    }
    if (row?.description) return row.description;
  }
  return "";
}

export function highestSeverity(item) {
  const order = { mild: 1, moderate: 2, severe: 3 };
  let best = "";
  let bestN = 0;
  for (const row of item?.reaction || []) {
    const n = order[row?.severity] || 0;
    if (n > bestN) {
      bestN = n;
      best = row.severity;
    }
  }
  return best;
}

export function formatStatus(code) {
  return STATUS_LABELS[code] || code || "—";
}

export function formatVerification(code) {
  return VERIFICATION_LABELS[code] || code || "—";
}

export function formatCriticality(code) {
  return CRITICALITY_LABELS[code] || "";
}

export function formatCategory(value) {
  const items = Array.isArray(value) ? value : value ? [value] : [];
  return items.map((code) => CATEGORY_LABELS[code] || code).join(", ");
}

export function formatSeverity(code) {
  return SEVERITY_LABELS[code] || code || "—";
}

export function formatWhen(value) {
  if (!value) return "—";
  const text = String(value);
  if (text.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return text;
}

export function formatReviewedAt(value) {
  const day = formatWhen(value);
  if (day === "—") return "";
  return day;
}

export function selectedFromAllergy(item) {
  if (!item) return null;
  return {
    id: item.allergenId,
    displayName: item.allergenDisplayName,
    allergenType: item.allergenType,
    allergenTypeLabel: allergenTypeLabel(item.allergenType),
  };
}

export function toAllergyPayload(form) {
  const kind = form.kind || ALLERGY_KIND_ALLERGY;
  const category = form.category || undefined;
  const payload = {
    allergenId: form.allergenId,
    type: kind === ALLERGY_KIND_INTOLERANCE ? "intolerance" : "allergy",
    al1Type: defaultAl1Type(category, kind),
    clinicalStatus: form.clinicalStatus || "active",
    verificationStatus: form.verificationStatus || "unconfirmed",
  };
  if (category) payload.category = category;
  if (form.criticality) payload.criticality = form.criticality;
  if (form.onsetDate) payload.onsetDate = form.onsetDate;
  if (form.note) payload.note = form.note;
  const manifestation = (form.manifestation || "").trim();
  if (manifestation) {
    payload.reaction = {
      manifestation,
      severity: form.severity || undefined,
      description: (form.description || "").trim() || undefined,
      exposureRoute: (form.exposureRoute || "").trim() || undefined,
    };
  } else if (form.clearReaction) {
    payload.reaction = [];
  }
  return payload;
}

export function allergyApiError(error, fallback = "Unable to update allergies.") {
  const status = error?.response?.status;
  if (status === 401) return "You are not signed in. Sign in again to continue.";
  if (status === 409) {
    return "This allergen is already recorded as an active allergy for this patient.";
  }
  if (status === 404) return "This patient or allergy record was not found.";
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") {
    if (/no known allergies/i.test(detail)) return detail;
    if (/inactive/i.test(detail) && /allergen/i.test(detail)) {
      return "That allergen is inactive in the catalog and cannot be added.";
    }
    if (/allergen_master concept not found/i.test(detail)) {
      return "The selected allergen is not in the catalog.";
    }
    if (/category is required/i.test(detail)) {
      return "Choose a category for this allergen.";
    }
    if (/not found/i.test(detail)) return "This patient or allergy record was not found.";
    return fallback;
  }
  if (Array.isArray(detail)) {
    return "Some allergy fields are invalid. Check the form and try again.";
  }
  return fallback;
}

export function emptyForm() {
  return {
    allergenId: "",
    selected: null,
    kind: ALLERGY_KIND_ALLERGY,
    clinicalStatus: "active",
    verificationStatus: "unconfirmed",
    criticality: "",
    category: "",
    manifestation: "",
    severity: "",
    description: "",
    exposureRoute: "",
    onsetDate: "",
    note: "",
  };
}

export function formFromAllergy(item) {
  const reaction = (item?.reaction || [])[0] || {};
  const manifestation = firstManifestation(item);
  return {
    allergenId: item.allergenId,
    selected: selectedFromAllergy(item),
    kind: kindFromRecord(item),
    clinicalStatus: item.clinicalStatus || "active",
    verificationStatus: item.verificationStatus || "unconfirmed",
    criticality: item.criticality || "",
    category: (item.category && item.category[0]) || "",
    manifestation,
    severity: reaction.severity || highestSeverity(item) || "",
    description: reaction.description || "",
    exposureRoute: reaction.exposureRoute?.text || "",
    onsetDate: formatWhen(item.onsetDate) === "—" ? "" : formatWhen(item.onsetDate),
    note: item.note || "",
  };
}
